import { useEffect, useState } from 'react';
import { Button, PopUpModal } from 'shared-components';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ICreateSecret, ISecret } from 'interface/secretsInterface';
import { showNotification } from 'helper/common/commonFunctions';
import CommonTable, { HeadCell } from 'components/common/TableCommon';
import secretService from 'services/secretService';
import { SecretForm } from './secretForm';
import './style.scss';
import { getTimeAgo } from 'utils/helper';
import { RootState } from 'redux-setup/store';
import { useAppSelector } from 'hooks/reduxHooks';

export const Secrets = () => {
  const [secrets, setSecrets] = useState<ISecret[]>([]);
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [selectedSecret, setSelectedSecret] = useState<ISecret | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [page, setPage] = useState<number>(1);
  const limit = 25;

  const organizationId = useAppSelector(
    (state: RootState) => state.organization.id,
  );

  const handleAddEdit = (secret?: ISecret) => {
    setSelectedSecret(secret || null);
    setIsAddEditOpen(true);
  };

  const handleDelete = (secret: ISecret) => {
    setSelectedSecret(secret);
    setIsDeleteOpen(true);
  };

  const headCellList: HeadCell<ISecret>[] = [
    { id: 'Key', label: 'Key' },
    { id: 'Scope', label: 'Scope' },
    {
      id: 'UpdatedAt',
      label: 'Last Updated',
      cellRender: (row: ISecret) => getTimeAgo(row.UpdatedAt),
    },
    {
      id: 'Actions',
      label: 'Actions',
      cellRender: (row: ISecret) => (
        <div className="action-buttons">
          <EditIcon
            className="cursor-pointer"
            onClick={() => handleAddEdit(row)}
          />
          <DeleteIcon
            className="cursor-pointer"
            onClick={() => handleDelete(row)}
          />
        </div>
      ),
    },
  ];

  const fetchSecrets = async () => {
    const searchPayload = {
      OrganizationID: organizationId,
      Page: page - 1,
      Limit: limit,
      Descending: true,
      OrderBy: 'UpdatedAt',
    };

    const res = await secretService.searchSecrets(searchPayload);
    if (res.status === 200 && res.data?.Result) {
      if (page === 1) {
        setSecrets(res.data.Result);
      } else {
        setSecrets(prev => [...prev, ...res.data.Result]);
      }
    }
  };

  useEffect(() => {
    fetchSecrets();
  }, [page]);

  const handleSubmit = async (data: ICreateSecret) => {
    try {
      if (selectedSecret) {
        const updatePayload = {
          SecretID: selectedSecret.ID,
          OrganizationID: data.OrganizationID,
          Scope: data.Scope,
          Key: data.Key,
          Value: data.Value,
        };
        await secretService.updateSecret(updatePayload);
        showNotification('success', 'Secret updated successfully');
      } else {
        await secretService.createSecret(data);
        showNotification('success', 'Secret created successfully');
      }
      setIsAddEditOpen(false);
      fetchSecrets();
    } catch (error) {
      showNotification('error', 'Failed to save secret');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (selectedSecret) {
        const deletePayload = {
          ID: selectedSecret.ID,
          OrganizationID: selectedSecret.OrganizationID,
        };
        await secretService.deleteSecret(deletePayload);
        showNotification('success', 'Secret deleted successfully');
        setIsDeleteOpen(false);
        setPage(1);
        setSecrets([]);
        await fetchSecrets();
      }
    } catch (error) {
      showNotification('error', 'Failed to delete secret');
    }
  };

  return (
    <div className="secrets-container">
      <div className="secrets-header">
        <Button
          label="Add Secret"
          variant="reliant"
          onClick={() => handleAddEdit()}
        />
      </div>

      <CommonTable<ISecret>
        headCellList={headCellList}
        data={secrets}
        isNextAPIDisabled={!secrets || secrets.length < limit}
        onEndReached={() => {
          setPage(prev => prev + 1);
        }}
      />

      {isAddEditOpen && (
        <SecretForm
          secret={selectedSecret}
          onSubmit={handleSubmit}
          onClose={() => setIsAddEditOpen(false)}
        />
      )}

      <PopUpModal
        open={isDeleteOpen}
        handleClose={() => setIsDeleteOpen(false)}
        heading="Delete Secret"
        buttons={[
          {
            buttonType: 'negative',
            buttonLabel: 'Cancel',
            buttonFunction: () => setIsDeleteOpen(false),
          },
          {
            buttonType: 'positive',
            buttonLabel: 'Delete',
            buttonFunction: handleConfirmDelete,
          },
        ]}>
        <div className="popup-section">
          <p>Are you sure you want to delete this secret?</p>
        </div>
      </PopUpModal>
    </div>
  );
};
